import type { ClientIOMode } from '@orborix/voice-client';
import {
  CONVERSATION_PREFS_DEFAULTS,
  CONVERSATION_SPEED_MAX,
  CONVERSATION_SPEED_MIN,
  type ConversationPrefs,
} from '@orborix/shared-types';

/**
 * Tipos, bancos de opciones y helpers de las settings de conversación.
 *
 * El selector de "modo de conversación" (vv/vt/tv/tt) MURIÓ como UI: el único
 * eje que quedaba vivo era el OUTPUT del coach (voz/texto), y ahora lo maneja
 * el toggle de voz del coach en el header (altavoz / altavoz tachado). El
 * campo `mode` de `ConversationPrefs` sigue siendo el storage (SIN tocar el
 * backend): unmuted ↔ `'vv'`, muted ↔ `'vt'`; las prefs viejas se migran al
 * leer (`normalizeConvMode`: vv/tv → vv, vt/tt → vt). El modo sólo-texto
 * (`tt`) murió como opción — el composer es SIEMPRE dual (campo + mic) y
 * `micHidden` quedó reservado al mic denegado. El viejo `micMode`
 * (tap/hold/auto/open) murió con la adopción de `@orborix/voice-composer`:
 * la sesión es SIEMPRE `captureMode: 'hold'` (push-to-talk) y el modo charla
 * vive en `useFreeChat`. Ambos campos legacy siguen existiendo en
 * `ConversationPrefs` (contrato de DB, tolerado si el backend los manda).
 *
 * La VELOCIDAD usa los valores reales del package (`SPEECH_RATE_VALUES`,
 * 0.7–1.2) con labels amigables vía `formatSpeechRateLabel`; no inventamos
 * números nuevos (`0.5×` es el label de `0.7`). La VOZ del coach ya no se
 * hardcodea acá: viene del catálogo de personas reales (ver `coachCatalog.ts`
 * + `hooks/useCoachPicker.ts`).
 *
 * `ConvSettings` es exactamente el contrato `ConversationPrefs` de
 * `@orborix/shared-types` (single source of truth: enums + defaults + schema
 * Zod). Estas prefs se persisten por-usuario en la DB (ver `englishApi`
 * `getConversationPrefs`/`saveConversationPrefs`). NO redefinir los unions
 * acá — derivarlos del contrato evita el tipo de drift que rompió el toggle
 * de PA (`'on'` vs `true`).
 */

export type ConvModeId = ConversationPrefs['mode'];
export type IntensityId = ConversationPrefs['intensity'];
export type DensityId = ConversationPrefs['density'];
export type RegisterId = ConversationPrefs['register'];
export type RegionId = ConversationPrefs['region'];
export type ToneId = ConversationPrefs['tone'];

export type ConvSettings = ConversationPrefs;

export const CONV_DEFAULTS: ConvSettings = CONVERSATION_PREFS_DEFAULTS;

// La intensidad del coach (6 paradas starter→native) la maneja el control de
// nivelación (`screens/leveling/IntensityControl` + `useEnglishLevel`), que es
// la fuente de verdad: lee `recommendedIntensity`/`effectiveIntensity` de
// `/state` y persiste vía `POST /level`. Acá ya no se redefine la escala.

export interface RegisterOption {
  id: RegisterId;
  label: string;
  desc: string;
}

/** Registro / estilo social del coach (ortogonal a la intensidad/dificultad). */
export const REGISTER_OPTIONS: ReadonlyArray<RegisterOption> = [
  { id: 'neutral', label: 'Neutro', desc: 'Inglés conversacional estándar, ni formal ni callejero.' },
  { id: 'casual', label: 'Casual', desc: 'Relajado, como un amigo: contracciones y muletillas.' },
  { id: 'slang', label: 'Slang', desc: 'Jerga y modismos actuales, glosados la primera vez.' },
  { id: 'formal', label: 'Formal', desc: 'Registro de oficina: pulido, cortés, sin contracciones.' },
  { id: 'academic', label: 'Académico', desc: 'Preciso y estructurado, conectores formales.' },
];

export interface RegionOption {
  id: RegionId;
  label: string;
  desc: string;
}

/**
 * «Tu inglés» (encargo 111): la variedad que aprende el alumno. Manda sobre el
 * coach (su voz la sigue), las correcciones y la pronunciación — ver
 * `english-variety.ts` de shared-types. Reemplazó a «Variedad» (sólo léxico).
 */
export const REGION_OPTIONS: ReadonlyArray<RegionOption> = [
  // Espacio duro en «EE. UU.»: en la tarjeta angosta se partía en dos renglones.
  { id: 'american', label: 'EE.\u00a0UU.', desc: 'Coach y correcciones de EE.\u00a0UU.' },
  { id: 'british', label: 'Reino Unido', desc: 'Coach y correcciones británicas.' },
  { id: 'australian', label: 'Australia', desc: 'Coach y correcciones australianas.' },
];

export interface ToneOption {
  id: ToneId;
  label: string;
  desc: string;
  /** i18n-ready: la UI hoy es es-first; el par en inglés queda listo. */
  labelEn: string;
  descEn: string;
}

/**
 * Tono / personalidad — cómo te trata el coach (no qué inglés usa). Con Gemini
 * TTS además cambia CÓMO SUENA la voz (identidad base del style, backend
 * `voice/tts-defaults.ts`). Quedan los 4 que se distinguen de verdad: el resto
 * se podó el 2026-10-04 (ver `CONV_TONES` en shared-types).
 */
export const TONE_OPTIONS: ReadonlyArray<ToneOption> = [
  {
    id: 'warm',
    label: 'Cálido',
    desc: 'Alentador y paciente, celebra tus avances.',
    labelEn: 'Warm',
    descEn: 'Encouraging and patient, celebrates your progress.',
  },
  {
    id: 'playful',
    label: 'Con humor',
    desc: 'Liviano, bromea, juega.',
    labelEn: 'Playful',
    descEn: 'Light-hearted, jokes around, plays.',
  },
  {
    id: 'demanding',
    label: 'Exigente',
    desc: 'Te sube la vara y te empuja a estirarte.',
    labelEn: 'Demanding',
    descEn: 'Raises the bar and pushes you to stretch.',
  },
  {
    id: 'professional',
    label: 'Profesional',
    desc: 'Pulido y preciso, inglés de trabajo.',
    labelEn: 'Professional',
    descEn: 'Polished and precise, workplace English.',
  },
];

/**
 * Frase del demo de tono: la MISMA para todos los tonos, así lo único que cambia
 * al elegir es la entrega (identidad de la voz). Es una apertura de charla, no
 * de clase: el coach de speak es un compañero de conversación, no un profe que
 * anuncia "let's practice".
 */
export const TONE_DEMO_TEXT = 'Hey, great to see you! How was your week? Tell me a little about it.';

export type SpanishVariantId = NonNullable<ConversationPrefs['spanishVariant']>;

export interface SpanishVariantOption {
  id: SpanishVariantId;
  flag: string;
  label: string;
  /** Muestra corta del tratamiento, para que la elección se entienda sin oírla. */
  desc: string;
}

/**
 * "Español del profe": variedad del español de las EXPLICACIONES (andamiaje,
 * ayudas, tips). No toca el inglés que se practica. El orden y los ids salen
 * del contrato (`SPANISH_VARIANTS` de shared-types).
 */
export const SPANISH_VARIANT_OPTIONS: ReadonlyArray<SpanishVariantOption> = [
  { id: 'ar', flag: '🇦🇷', label: 'Argentina (voseo)', desc: '«Decime», «mirá», «dale»' },
  { id: 'mx', flag: '🇲🇽', label: 'México', desc: '«Dime», «órale», «¿sale?»' },
  { id: 'co', flag: '🇨🇴', label: 'Colombia', desc: '«Usted me dice», «chévere»' },
  { id: 'es', flag: '🇪🇸', label: 'España', desc: '«Dime», «vale», «venga»' },
  { id: 'cl', flag: '🇨🇱', label: 'Chile', desc: '«Dime», «¿cachai?», «al tiro»' },
  { id: 'neutral', flag: '🌎', label: 'Neutro', desc: 'Latinoamericano, sin modismos' },
];

/**
 * Demo bilingüe por variedad (≤ PREVIEW_MAX_CHARS del backend = 240): una
 * línea de andamiaje en esa variedad + la frase en inglés, como la diría el
 * coach. Textos de la PoC regional v2, acortados.
 */
export const SPANISH_VARIANT_DEMO_TEXT: Readonly<Record<SpanishVariantId, string>> = {
  ar: "¡Hola! Mirá, esto es re fácil. Decime tu nombre en inglés así: My name is… ¿Dale? Animate. What's your name?",
  mx: "¡Hola! Mira, esto está bien padre y bien fácil. Nomás dime tu nombre en inglés así: My name is… ¿Sale? What's your name?",
  co: "¡Hola, con mucho gusto! Esto es muy chévere y muy fácil. Usted me dice su nombre en inglés así: My name is… ¿Listo? What's your name?",
  es: "¡Hola! Venga, que esto es facilísimo, ya verás. Dime tu nombre en inglés así: My name is… ¿Vale? What's your name?",
  cl: "¡Hola! Oye, esto es súper fácil, ¿cachai? Me dices tu nombre en inglés así: My name is… ¿Ya? What's your name?",
  neutral: "¡Hola! Mira, esto es muy fácil. Dime tu nombre en inglés así: My name is… ¡Tú puedes! What's your name?",
};

export interface DensityOption {
  id: DensityId;
  label: string;
}

/**
 * Remapeo de chat3: `compact` es el más chico, `normal` (default) es el viejo
 * "compacto", y `grande/Cómoda` es el viejo "normal".
 */
export const DENSITY_OPTIONS: ReadonlyArray<DensityOption> = [
  { id: 'compact', label: 'Compacta' },
  { id: 'normal', label: 'Normal' },
  { id: 'grande', label: 'Cómoda' },
];

/** Rango oficial del package (ElevenLabs preserva pitch nativo en 0.7–1.2). */
export const SPEED_MIN = CONVERSATION_SPEED_MIN;
export const SPEED_MAX = CONVERSATION_SPEED_MAX;

/**
 * Fallback de voiceId (ElevenLabs "Rachel") para cuando aún no se resolvió el
 * catálogo de personas — p.ej. para previsualizar sugerencias de Inspire antes
 * de que cargue el CoachPicker.
 */
export const FALLBACK_VOICE_ID = '21m00Tcm4TlvDq8ikWAM';

/**
 * ¿El coach responde SÓLO por texto? Derivado del `mode` persistido (storage
 * del toggle de voz del coach): `vt`/`tt` = muteado, `vv`/`tv` = con voz.
 * Tolera los 4 valores legacy aunque la UI sólo escriba `vv`/`vt`.
 */
export function coachMutedFromMode(mode: ConvModeId): boolean {
  return mode === 'vt' || mode === 'tt';
}

/**
 * Mode canónico que ESCRIBE el toggle de voz del coach: unmuted ↔ `'vv'`,
 * muted ↔ `'vt'`. El backend sigue guardando `mode` sin cambios de contrato.
 */
export function modeForCoachMuted(muted: boolean): ConvModeId {
  return muted ? 'vt' : 'vv';
}

/**
 * Migración de prefs viejas AL LEER (sin tocar el backend): colapsa los 4
 * modos legacy al par vigente — `vv`/`tv` → `'vv'` (con voz), `vt`/`tt` →
 * `'vt'` (sólo texto). Se persiste normalizado en el próximo PUT de prefs.
 */
export function normalizeConvMode(mode: ConvModeId): ConvModeId {
  return modeForCoachMuted(coachMutedFromMode(mode));
}

/**
 * IO real de la SESIÓN con el composer compartido (`@orborix/voice-composer`).
 *
 * El composer es SIEMPRE dual (campo de texto + mic hold — estilo WhatsApp,
 * diseño aprobado), así que el input es SIEMPRE `'voice'` (el voice-client
 * sólo arma el mic con `input === 'voice'`; el texto viaja por `sendText`
 * en cualquier caso). El output es el único eje del usuario: el toggle de
 * voz del coach (muted → `'text'`, sin TTS → sin costo ElevenLabs).
 */
export function modeToSessionIo(mode: ConvModeId): ClientIOMode {
  return { input: 'voice', output: coachMutedFromMode(mode) ? 'text' : 'voice' };
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
